import { get, writable } from 'svelte/store';

import preloadAssets, { loadFont } from '../lib/assets.js';
import { loadImage, readFileAsPdf, readFileAsDataUrl } from '../lib/fileReaders.js';
import { savePdf } from '../lib/pdf.js';
import { createIdGenerator } from '../lib/utils.js';
import {
  DEFAULT_FONT_FAMILY,
  DEFAULT_TEXT_FONT_SIZE,
  DEFAULT_TEXT_LINE_HEIGHT,
  DEFAULT_TEXT_VALUE,
  OBJECT_TYPE_DRAWING,
  OBJECT_TYPE_IMAGE,
  OBJECT_TYPE_TEXT,
  OBJECT_TYPE_SHAPE,
  PDF_MIME_TYPE,
  MAX_OBJECT_DIMENSION,
} from '../lib/constants.js';

const generateId = createIdGenerator();

function createInitialState(overrides = {}) {
  return {
    pdfSourceFile: null,
    pdfFileName: '',
    pages: [],
    pageScales: [],
    elements: [],
    activeFontFamily: DEFAULT_FONT_FAMILY,
    currentPageIndex: -1,
    selectedPageIndex: null,
    selectedElementIds: [],
    history: [],
    historyIndex: -1,
    isSaving: false,
    isDrawingCanvasOpen: false,
    zoomLevel: 1.0,
    pdfRenderScale: 0.75,
    hideObjectBorders: false,
    imageCompressQuality: 0.8,
    ...overrides,
  };
}

function isValidPageIndex(state, pageIndex) {
  return pageIndex >= 0 && pageIndex < state.pages.length;
}

function createEditorStore() {
  const store = writable(createInitialState());
  const { subscribe, set, update } = store;

  const getState = () => get(store);

  function saveHistory(state, elements) {
    const history = state.history.slice(0, state.historyIndex + 1);
    history.push(elements);
    if (history.length > 50) history.shift();
    return {
      history,
      historyIndex: history.length - 1,
    };
  }

  function init() {
    preloadAssets();
    loadFont(getState().activeFontFamily);
  }

  function resetDocument() {
    set(createInitialState({ activeFontFamily: getState().activeFontFamily }));
  }

  async function loadPdf(pdfFile) {
    if (!pdfFile || pdfFile.type !== PDF_MIME_TYPE) return;

    resetDocument();

    const pdf = await readFileAsPdf(pdfFile);
    const pages = Array.from({ length: pdf.numPages }, (_, index) => pdf.getPage(index + 1));

    update((state) => {
      const initialObjects = pages.map(() => []);

      const scales = Array(pages.length).fill(state.pdfRenderScale);

      return {
        ...state,
        pdfSourceFile: pdfFile,
        pdfFileName: pdfFile.name || 'document.pdf',
        pages,
        elements: initialObjects,
        pageScales: scales,
        currentPageIndex: pages.length ? 0 : -1,
        history: [initialObjects],
        historyIndex: 0,
      }
    });
  }

  async function loadTestPdf() {
    try {
      const response = await fetch('/test.pdf');
      if (!response.ok) return;
      const blob = await response.blob();
      const file = new File([blob], 'test.pdf', { type: PDF_MIME_TYPE });
      await loadPdf(file);
    } catch (e) {
    }
  }

  function setPdfFileName(pdfFileName) {
    update((state) => ({ ...state, pdfFileName }));
  }

  function setActivePage(pageIndex) {
    update((state) => ({ ...state, currentPageIndex: pageIndex }));
  }

  function setZoomLevel(zoom) {
    update((state) => ({ ...state, zoomLevel: Math.max(0.1, Math.min(5.0, zoom)) }));
  }

  function setPdfRenderScale(scale) {
    update((state) => {
      const pageScales = state.pages.map(() => scale);
      return { ...state, pdfRenderScale: scale, pageScales };
    });
  }

  function setHideObjectBorders(hide) {
    update((state) => ({ ...state, hideObjectBorders: hide }));
  }

  function setImageCompressQuality(quality) {
    update((state) => ({ ...state, imageCompressQuality: quality }));
  }

  function selectObject({ pageIndex, id, multi = false }) {
    update((state) => {
      if (state.selectedPageIndex !== pageIndex) {
        state.selectedElementIds = [];
      }

      let newSelection = multi ? [...state.selectedElementIds] : [];

      const elementsOnPage = state.elements[pageIndex] || [];
      const targetObj = elementsOnPage.find(o => o.id === id);

      if (targetObj) {
        if (targetObj.groupId) {
          const groupItems = elementsOnPage.filter(o => o.groupId === targetObj.groupId).map(o => o.id);
          groupItems.forEach(gid => {
            if (!newSelection.includes(gid)) newSelection.push(gid);
          });
        } else {
          if (!newSelection.includes(id)) newSelection.push(id);
        }
      }

      return { ...state, selectedPageIndex: pageIndex, selectedElementIds: newSelection };
    });
  }

  function clearSelection() {
    update((state) => ({ ...state, selectedPageIndex: null, selectedElementIds: [] }));
  }

  function setPageScale(pageIndex, scale) {
    update((state) => {
      if (!isValidPageIndex(state, pageIndex)) return state;
      const pageScales = state.pageScales.slice();
      pageScales[pageIndex] = scale;
      return { ...state, pageScales };
    });
  }

  function setActiveFontFamily(fontFamily) {
    if (!fontFamily) return;
    loadFont(fontFamily);
    update((state) => ({ ...state, activeFontFamily: fontFamily }));
  }

  function addObjectToActivePage(newObject) {
    update((state) => {
      if (!isValidPageIndex(state, state.currentPageIndex)) return state;

      const elements = state.elements.map((objects, index) =>
        index === state.currentPageIndex ? [...objects, newObject] : objects,
      );

      return { ...state, elements, ...saveHistory(state, elements) };
    });
  }

  async function addImage({ file, compress = false, quality = 0.8 }) {
    if (!file) return;
    const state = getState();
    if (!isValidPageIndex(state, state.currentPageIndex)) return;

    let url = await readFileAsDataUrl(file);
    let image = await loadImage(url);
    let finalFile = file;

    if (compress || !['image/jpeg', 'image/png'].includes(file.type)) {
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth || image.width;
      canvas.height = image.naturalHeight || image.height;
      const ctx = canvas.getContext('2d');
      if (compress && file.type === 'image/jpeg') {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      ctx.drawImage(image, 0, 0);

      const mimeType = compress ? 'image/jpeg' : 'image/png';
      const compressionRate = compress ? Math.max(0.1, Math.min(1.0, quality)) : undefined;
      const compressedDataUrl = canvas.toDataURL(mimeType, compressionRate);
      url = compressedDataUrl;
      const blob = await (await fetch(compressedDataUrl)).blob();
      const ext = compress ? '.jpg' : '.png';
      finalFile = new File([blob], file.name.replace(/\.[^/.]+$/, "") + ext, { type: mimeType });
      image = await loadImage(url);
    }

    let scale = 1;
    if (image.width > MAX_OBJECT_DIMENSION) scale = MAX_OBJECT_DIMENSION / image.width;
    if (image.height > MAX_OBJECT_DIMENSION) scale = Math.min(scale, MAX_OBJECT_DIMENSION / image.height);

    clearSelection();
    addObjectToActivePage({
      id: generateId(),
      type: OBJECT_TYPE_IMAGE,
      width: image.width * scale,
      height: image.height * scale,
      x: 0,
      y: 0,
      image,
      imageFile: finalFile,
      keepRatio: true,
    });
  }

  function addShape() {
    const state = getState();
    if (!isValidPageIndex(state, state.currentPageIndex)) return;

    clearSelection();
    addObjectToActivePage({
      id: generateId(),
      type: OBJECT_TYPE_SHAPE,
      shapeType: 'rect',
      width: 100,
      height: 100,
      x: 0,
      y: 0,
      keepRatio: false,
      fillColor: 'transparent',
      strokeColor: '#000000',
      strokeWidth: 5,
      borderRadius: 0,
    });
  }

  function addText(text = DEFAULT_TEXT_VALUE) {
    const state = getState();
    if (!isValidPageIndex(state, state.currentPageIndex)) return;

    loadFont(state.activeFontFamily);

    clearSelection();
    addObjectToActivePage({
      id: generateId(),
      type: OBJECT_TYPE_TEXT,
      html: text,
      lines: [text],
      fontSize: DEFAULT_TEXT_FONT_SIZE,
      lineHeight: DEFAULT_TEXT_LINE_HEIGHT,
      fontFamily: state.activeFontFamily,
      color: '#000000',
      width: 0,
      x: 0,
      y: 0,
      scaleX: 1,
      scaleY: 1,
    });
  }

  function openDrawingCanvas() {
    update((state) => ({ ...state, isDrawingCanvasOpen: true }));
  }

  function closeDrawingCanvas() {
    update((state) => ({ ...state, isDrawingCanvasOpen: false }));
  }

  function addDrawing({ originalWidth, originalHeight, svgPath, scale = 1 }) {
    const state = getState();
    if (!isValidPageIndex(state, state.currentPageIndex)) return;

    clearSelection();
    addObjectToActivePage({
      id: generateId(),
      type: OBJECT_TYPE_DRAWING,
      svgPath,
      originalWidth,
      originalHeight,
      width: originalWidth * scale,
      scale,
      x: 0,
      y: 0,
    });
  }

  function patchObject({ pageIndex, id, updates }) {
    update((state) => {
      if (!isValidPageIndex(state, pageIndex)) return state;

      const elements = state.elements.map((objects, index) => {
        if (index !== pageIndex) return objects;

        let targetObj = objects.find(o => o.id === id);
        if (!targetObj) return objects;

        let dx = updates.x !== undefined ? updates.x - targetObj.x : 0;
        let dy = updates.y !== undefined ? updates.y - targetObj.y : 0;

        return objects.map((object) => {
          if (object.id === id) {
            return { ...object, ...updates };
          }
          if (state.selectedElementIds.includes(object.id)) {
            if (dx !== 0 || dy !== 0) {
              return { ...object, x: object.x + dx, y: object.y + dy };
            }
          }
          return object;
        });
      });

      return { ...state, elements, ...saveHistory(state, elements) };
    });
  }

  function updateObjectDraft({ pageIndex, id, updates }) {
    update((state) => {
      if (!isValidPageIndex(state, pageIndex)) return state;

      const elements = state.elements.map((objects, index) => {
        if (index !== pageIndex) return objects;

        let targetObj = objects.find(o => o.id === id);
        if (!targetObj) return objects;

        let dx = updates.x !== undefined ? updates.x - targetObj.x : 0;
        let dy = updates.y !== undefined ? updates.y - targetObj.y : 0;

        return objects.map((object) => {
          if (object.id === id) {
            return { ...object, ...updates };
          }
          if (state.selectedElementIds.includes(object.id)) {
            if (dx !== 0 || dy !== 0) {
              return { ...object, x: object.x + dx, y: object.y + dy };
            }
          }
          return object;
        });
      });

      return { ...state, elements };
    });
  }

  function removeObject({ pageIndex }) {
    update((state) => {
      if (!isValidPageIndex(state, pageIndex)) return state;

      const idsToRemove = state.selectedElementIds;
      if (idsToRemove.length === 0) return state;

      const elements = state.elements.map((objects, index) =>
        index === pageIndex ? objects.filter((object) => !idsToRemove.includes(object.id)) : objects,
      );

      return { ...state, elements, selectedElementIds: [], ...saveHistory(state, elements) };
    });
  }

  function cloneObject({ pageIndex }) {
    update((state) => {
      if (!isValidPageIndex(state, pageIndex)) return state;

      const idsToClone = state.selectedElementIds;
      if (idsToClone.length === 0) return state;

      let newSelectedIds = [];
      const elements = state.elements.map((objects, index) => {
        if (index === pageIndex) {
          const clonedObjects = objects
            .filter(o => idsToClone.includes(o.id))
            .map(obj => {
              const newId = generateId();
              newSelectedIds.push(newId);
              return { ...obj, id: newId, x: obj.x + 20, y: obj.y + 20 };
            });
          return [...objects, ...clonedObjects];
        }
        return objects;
      });

      return { ...state, elements, selectedElementIds: newSelectedIds, ...saveHistory(state, elements) };
    });
  }

  function changeObjectZIndex({ pageIndex, action }) {
    update((state) => {
      if (!isValidPageIndex(state, pageIndex)) return state;

      const targetIds = state.selectedElementIds;
      if (targetIds.length === 0) return state;

      const elements = state.elements.map((objects, index) => {
        if (index === pageIndex) {
          const newObjects = [...objects];

          const selectedItems = [];
          const remainingItems = [];

          for (const obj of newObjects) {
            if (targetIds.includes(obj.id)) {
              selectedItems.push(obj);
            } else {
              remainingItems.push(obj);
            }
          }

          if (action === 'bringToFront') {
            return [...remainingItems, ...selectedItems];
          } else if (action === 'sendToBack') {
            return [...selectedItems, ...remainingItems];
          }
          else if (action === 'moveForward' || action === 'moveBackward') {
            if (targetIds.length > 1) {
              return action === 'moveForward' ? [...remainingItems, ...selectedItems] : [...selectedItems, ...remainingItems];
            } else {
              const id = targetIds[0];
              const objIndex = newObjects.findIndex(o => o.id === id);
              if (objIndex === -1) return newObjects;
              const [obj] = newObjects.splice(objIndex, 1);
              if (action === 'moveForward') {
                newObjects.splice(Math.min(objects.length - 1, objIndex + 1), 0, obj);
              } else {
                newObjects.splice(Math.max(0, objIndex - 1), 0, obj);
              }
              return newObjects;
            }
          }
        }
        return objects;
      });

      return { ...state, elements, ...saveHistory(state, elements) };
    });
  }

  function bringToFront({ pageIndex }) {
    changeObjectZIndex({ pageIndex, action: 'bringToFront' });
  }

  function sendToBack({ pageIndex }) {
    changeObjectZIndex({ pageIndex, action: 'sendToBack' });
  }

  function moveForward({ pageIndex }) {
    changeObjectZIndex({ pageIndex, action: 'moveForward' });
  }

  function moveBackward({ pageIndex }) {
    changeObjectZIndex({ pageIndex, action: 'moveBackward' });
  }

  function groupSelected() {
    update((state) => {
      if (state.selectedElementIds.length < 2) return state;
      const groupId = generateId();

      const elements = state.elements.map((objects, index) => {
        if (index === state.selectedPageIndex) {
          return objects.map(obj => {
            if (state.selectedElementIds.includes(obj.id)) {
              return { ...obj, groupId };
            }
            return obj;
          });
        }
        return objects;
      });
      return { ...state, elements, ...saveHistory(state, elements) };
    });
  }

  function ungroupSelected() {
    update((state) => {
      if (state.selectedElementIds.length === 0) return state;

      const elements = state.elements.map((objects, index) => {
        if (index === state.selectedPageIndex) {
          return objects.map(obj => {
            if (state.selectedElementIds.includes(obj.id)) {
              const newObj = { ...obj };
              delete newObj.groupId;
              return newObj;
            }
            return obj;
          });
        }
        return objects;
      });
      return { ...state, elements, ...saveHistory(state, elements) };
    });
  }

  function alignSelected(direction) {
    update((state) => {
      const selectedIds = state.selectedElementIds;
      if (selectedIds.length < 2) return state;

      const pageIndex = state.selectedPageIndex;
      if (pageIndex === null) return state;

      const elements = state.elements.map((objects, index) => {
        if (index !== pageIndex) return objects;

        const selectedObjs = objects.filter(o => selectedIds.includes(o.id));
        if (selectedObjs.length < 2) return objects;

        let minX = Infinity, minY = Infinity;
        let maxX = -Infinity, maxY = -Infinity;

        selectedObjs.forEach(o => {
          minX = Math.min(minX, o.x);
          minY = Math.min(minY, o.y);
          maxX = Math.max(maxX, o.x + (o.width || 0));
          maxY = Math.max(maxY, o.y + (o.height || 0));
        });

        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;

        return objects.map(obj => {
          if (!selectedIds.includes(obj.id)) return obj;

          let newX = obj.x;
          let newY = obj.y;

          switch (direction) {
            case 'left': newX = minX; break;
            case 'right': newX = maxX - (obj.width || 0); break;
            case 'centerH': newX = centerX - (obj.width || 0) / 2; break;
            case 'top': newY = minY; break;
            case 'bottom': newY = maxY - (obj.height || 0); break;
            case 'centerV': newY = centerY - (obj.height || 0) / 2; break;
          }
          return { ...obj, x: newX, y: newY };
        });
      });

      return { ...state, elements, ...saveHistory(state, elements) };
    });
  }

  async function save({ saveAsPng = false, zipPngs = false, pagesToExport } = {}) {
    let state;
    update((s) => {
      state = s;
      return { ...s, isSaving: true };
    });

    if (!state.pdfSourceFile) return;

    try {
      await savePdf({
        sourceFile: state.pdfSourceFile,
        pageObjects: state.elements,
        outputFileName: state.pdfFileName || 'document',
        saveAsPng,
        zipPngs,
        pagesToExport
      });
    } finally {
      update((s) => ({ ...s, isSaving: false }));
    }
  }

  function undo() {
    update((state) => {
      if (state.historyIndex <= 0) return state;
      const newIndex = state.historyIndex - 1;
      return {
        ...state,
        historyIndex: newIndex,
        elements: state.history[newIndex],
      };
    });
  }

  function redo() {
    update((state) => {
      if (state.historyIndex >= state.history.length - 1) return state;
      const newIndex = state.historyIndex + 1;
      return {
        ...state,
        historyIndex: newIndex,
        elements: state.history[newIndex],
      };
    });
  }

  return {
    subscribe,
    init,
    loadPdf,
    loadTestPdf,
    setPdfFileName,
    setActivePage,
    setPdfRenderScale,
    setImageCompressQuality,
    selectObject,
    clearSelection,
    setPageScale,
    setActiveFontFamily,
    addImage,
    addText,
    addShape,
    openDrawingCanvas,
    closeDrawingCanvas,
    addDrawing,
    patchObject,
    updateObjectDraft,
    removeObject,
    cloneObject,
    bringToFront,
    sendToBack,
    moveForward,
    moveBackward,
    groupSelected,
    ungroupSelected,
    alignSelected,
    save,
    undo,
    redo,
    setZoomLevel,
    setHideObjectBorders,
  };
}

export const editor = createEditorStore();
